// src/pages/SolutionPage.tsx
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { client } from "../sanity/client";
import Title from "../components/typography/Title";
import Subtitle from "../components/typography/Subtitle";
import Description from "../components/typography/Description";
import Button from "../shared/button/Button";

type MediaBlock = {
  kind?: "image" | "videoFile" | "audioFile" | "embedUrl" | "gifUrl" | "icon";
  title?: string;
  caption?: string;
  alt?: string;
  image?: any;
  posterImage?: any;
  videoFile?: any;
  audioFile?: any;
  embedUrl?: string;
  gifUrl?: string;
  iconName?: string;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
};

type CTA = {
  label?: string;
  href?: string;
  variant?: "primary" | "secondary";
};

type SolutionHero = {
  industry?: string;
  headline?: string;
  subheadline?: string;
  heroImage?: MediaBlock;
  ctas?: CTA[];
};

type Challenge = {
  title?: string;
  description?: string;
  icon?: string;
};

type Benefit = {
  title?: string;
  description?: string;
  media?: MediaBlock;
};

type SuccessMetric = {
  label?: string;
  value?: string;
  context?: string;
};

type ImplementationStep = {
  phase?: string;
  title?: string;
  description?: string;
  duration?: string;
};

type Testimonial = {
  quote?: string;
  author?: string;
  role?: string;
  company?: string;
  logo?: MediaBlock;
};

type BottomCTA = {
  heading?: string;
  subheading?: string;
  primaryCta?: CTA;
  secondaryCta?: CTA;
};

type ChallengesSection = {
  _type: "challengesSection";
  enabled?: boolean;
  heading?: string;
  challenges?: Challenge[];
};

type ApproachSection = {
  _type: "approachSection";
  enabled?: boolean;
  heading?: string;
  subheading?: string;
  benefits?: Benefit[];
};

type SuccessMetricsSection = {
  _type: "successMetricsSection";
  enabled?: boolean;
  heading?: string;
  metrics?: SuccessMetric[];
};

type ImplementationSection = {
  _type: "implementationSection";
  enabled?: boolean;
  heading?: string;
  subheading?: string;
  steps?: ImplementationStep[];
};

type TestimonialsSection = {
  _type: "testimonialsSection";
  enabled?: boolean;
  heading?: string;
  testimonials?: Testimonial[];
};

type SolutionBottomCtaSection = {
  _type: "solutionBottomCta";
  enabled?: boolean;
  heading?: string;
  subheading?: string;
  primaryCta?: CTA;
  secondaryCta?: CTA;
};

type SolutionSection =
  | ChallengesSection
  | ApproachSection
  | SuccessMetricsSection
  | ImplementationSection
  | TestimonialsSection
  | SolutionBottomCtaSection;

type SolutionDoc = {
  solutionName?: string;
  industry?: string;
  hero?: SolutionHero;
  sections?: SolutionSection[];
  slug?: { current?: string };

  // SEO
  tabTitle?: string;
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
  seoImage?: any;
  canonicalUrl?: string;
  noIndex?: boolean;
};

const SOLUTION_BY_SLUG = `
*[_type == "solutionPage" && slug.current == $slug][0]{
  solutionName,
  industry,

  hero{
    industry,
    headline,
    subheadline,
    heroImage{
      kind,
      title,
      caption,
      alt,
      image{asset->{url, metadata{dimensions}}},
      embedUrl,
      gifUrl,
      iconName
    },
    ctas[]{
      label,
      href,
      variant
    }
  },

  sections[]{
    _type,
    enabled,
    heading,
    subheading,

    challenges[]{
      title,
      description,
      icon
    },

    benefits[]{
      title,
      description,
      media{
        kind,
        title,
        caption,
        alt,
        image{asset->{url, metadata{dimensions}}},
        embedUrl,
        gifUrl,
        iconName
      }
    },

    metrics[]{
      label,
      value,
      context
    },

    steps[]{
      phase,
      title,
      description,
      duration
    },

    testimonials[]{
      quote,
      author,
      role,
      company,
      logo{
        kind,
        image{asset->{url, metadata{dimensions}}}
      }
    },

    primaryCta{
      label,
      href
    },
    secondaryCta{
      label,
      href
    }
  },

  tabTitle,
  seoTitle,
  seoDescription,
  seoKeywords,
  seoImage{asset->{url}},
  canonicalUrl,
  noIndex,

  slug
}
`;

type Accent = {
  pageBg: string;
  panelBg: string;
  accent: string;
  accA: string;
  accB: string;
  glow: string;
  border: string;
};

function getAccentBySolutionSlug(slug?: string): Accent {
  const key = (slug || "").toLowerCase();

  const palette: Record<string, Accent> = {
    enterprises: {
      pageBg: "#071220",
      panelBg: "#0A1825",
      accent: "#20B8CD",
      accA: "#20B8CD",
      accB: "#1B7683",
      glow: "rgba(32,184,205,0.35)",
      border: "rgba(32,184,205,0.22)",
    },
    startups: {
      pageBg: "#1A0F20",
      panelBg: "#251631",
      accent: "#C77DFF",
      accA: "#C77DFF",
      accB: "#9D4EDD",
      glow: "rgba(199,125,255,0.35)",
      border: "rgba(199,125,255,0.22)",
    },
    "recruitment-agencies": {
      pageBg: "#0A1810",
      panelBg: "#0F241B",
      accent: "#14AE5C",
      accA: "#14AE5C",
      accB: "#148F4E",
      glow: "rgba(20,174,92,0.35)",
      border: "rgba(20,174,92,0.22)",
    },
    institutions: {
      pageBg: "#121027",
      panelBg: "#19163A",
      accent: "#7AA7FF",
      accA: "#7AA7FF",
      accB: "#5182E8",
      glow: "rgba(122,167,255,0.35)",
      border: "rgba(122,167,255,0.22)",
    },
    "ai-labs": {
      pageBg: "#1A0A20",
      panelBg: "#2A1431",
      accent: "#FF6A00",
      accA: "#FF6A00",
      accB: "#D95700",
      glow: "rgba(255,106,0,0.35)",
      border: "rgba(255,106,0,0.22)",
    },
  };

  return (
    palette[key] || {
      pageBg: "#071220",
      panelBg: "#0A1825",
      accent: "#20B8CD",
      accA: "#20B8CD",
      accB: "#1B7683",
      glow: "rgba(32,184,205,0.35)",
      border: "rgba(32,184,205,0.22)",
    }
  );
}

function accentVars(accent: Accent) {
  return {
    "--pageBg": accent.pageBg,
    "--panelBg": accent.panelBg,
    "--acc": accent.accent,
    "--accA": accent.accA,
    "--accB": accent.accB,
    "--accGlow": accent.glow,
    "--accBorder": accent.border,
  } as React.CSSProperties;
}

function solidHeroBg(): React.CSSProperties {
  return {
    backgroundColor: "var(--pageBg)",
    backgroundImage: `
      radial-gradient(900px 520px at 18% 28%, var(--accGlow), transparent 60%),
      radial-gradient(760px 420px at 82% 72%, rgba(255,255,255,0.07), transparent 62%)
    `,
  } as React.CSSProperties;
}

function solidCtaBg(): React.CSSProperties {
  return {
    backgroundColor: "var(--pageBg)",
    backgroundImage: `
      radial-gradient(1000px 560px at 20% 40%, var(--accGlow), transparent 62%),
      radial-gradient(820px 520px at 85% 65%, rgba(255,255,255,0.08), transparent 66%)
    `,
  } as React.CSSProperties;
}

const SolutionPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [solution, setSolution] = useState<SolutionDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    client
      .fetch<SolutionDoc>(SOLUTION_BY_SLUG, { slug })
      .then((data) => {
        if (!data) {
          setError(`Solution "${slug}" not found`);
        } else {
          setSolution(data);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading solution data:", err);
        setError(`Solution "${slug}" not found`);
        setLoading(false);
      });
  }, [slug]);

  const accent = getAccentBySolutionSlug(slug);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-900">
        <div className="text-xl text-gray-300">Loading...</div>
      </div>
    );
  }

  if (error || !solution) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="mb-4 text-2xl text-white">Solution Not Found</div>
          <div className="text-gray-400">{error || "Unable to load solution"}</div>
        </div>
      </div>
    );
  }

  const pageTitle = solution.tabTitle || solution.solutionName;
  const seoTitle = solution.seoTitle || solution.solutionName;
  const seoDescription = solution.seoDescription || solution.hero?.subheadline;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {solution.seoKeywords && (
          <meta name="keywords" content={solution.seoKeywords.join(", ")} />
        )}
        {solution.noIndex && <meta name="robots" content="noindex" />}
        {solution.canonicalUrl && <link rel="canonical" href={solution.canonicalUrl} />}
      </Helmet>

      <div
        style={accentVars(accent)}
        className="min-h-screen bg-[var(--pageBg)] text-white"
      >
        {/* Hero Section */}
        <section style={solidHeroBg()} className="px-4 py-16 md:px-12 md:py-24 lg:py-32">
          <div className="mx-auto max-w-6xl text-center">
            <Subtitle className="mb-4 text-[var(--acc)]">
              {solution.hero?.industry}
            </Subtitle>
            <Title size="xl" className="mb-6">
              {solution.hero?.headline}
            </Title>
            <Description className="mx-auto mb-10 max-w-3xl text-lg">
              {solution.hero?.subheadline}
            </Description>

            {solution.hero?.ctas && solution.hero.ctas.length > 0 && (
              <div className="flex flex-wrap justify-center gap-4">
                {solution.hero.ctas.map((cta, idx) => (
                  <Link key={idx} to={cta.href || "#"}>
                    <Button
                      variant={cta.variant === "primary" ? "primary" : "secondary"}
                      size="lg"
                    >
                      {cta.label}
                    </Button>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Render Dynamic Sections */}
        {solution.sections?.map((section, idx) => {
          if (!section.enabled) return null;

          switch (section._type) {
            case "challengesSection":
              return (
                <section key={idx} className="px-4 py-16 md:px-12 md:py-20">
                  <div className="mx-auto max-w-6xl">
                    <Title size="lg" className="mb-12 text-center">
                      {section.heading || "Challenges We Solve"}
                    </Title>
                    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                      {section.challenges?.map((challenge, cIdx) => (
                        <div
                          key={cIdx}
                          className="rounded-2xl border border-[var(--accBorder)] bg-gray-900/50 p-8 backdrop-blur-lg transition-all hover:border-[var(--acc)]"
                        >
                          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[var(--acc)]/20">
                            <span className="text-2xl text-[var(--acc)]">•</span>
                          </div>
                          <h3 className="mb-3 font-serif text-2xl font-light">
                            {challenge.title}
                          </h3>
                          <p className="text-gray-350 leading-relaxed">
                            {challenge.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              );

            case "approachSection":
              return (
                <section key={idx} className="px-4 py-16 md:px-12 md:py-20">
                  <div className="mx-auto max-w-6xl">
                    <Title size="lg" className="mb-4 text-center">
                      {section.heading}
                    </Title>
                    {section.subheading && (
                      <Description className="mx-auto mb-12 max-w-3xl text-center">
                        {section.subheading}
                      </Description>
                    )}
                    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                      {section.benefits?.map((benefit, bIdx) => (
                        <div
                          key={bIdx}
                          className="rounded-xl border border-gray-550 bg-gray-800 p-6 transition-all hover:border-[var(--acc)]"
                        >
                          <h4 className="mb-3 font-serif text-xl font-light">
                            {benefit.title}
                          </h4>
                          <p className="text-gray-300 leading-relaxed">
                            {benefit.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              );

            case "successMetricsSection":
              return (
                <section key={idx} className="px-4 py-16 md:px-12 md:py-20">
                  <div className="mx-auto max-w-6xl">
                    <Title size="lg" className="mb-12 text-center">
                      {section.heading || "Proven Results"}
                    </Title>
                    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                      {section.metrics?.map((metric, mIdx) => (
                        <div key={mIdx} className="text-center">
                          <div className="mb-2 font-serif text-5xl font-light text-[var(--acc)]">
                            {metric.value}
                          </div>
                          <div className="mb-1 text-sm font-medium uppercase tracking-wide text-gray-300">
                            {metric.label}
                          </div>
                          {metric.context && (
                            <div className="text-xs text-gray-450">{metric.context}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              );

            case "implementationSection":
              return (
                <section key={idx} className="px-4 py-16 md:px-12 md:py-20">
                  <div className="mx-auto max-w-7xl">
                    <Title size="lg" className="mb-4 text-center">
                      {section.heading || "Implementation Roadmap"}
                    </Title>
                    {section.subheading && (
                      <Description className="mx-auto mb-12 max-w-3xl text-center text-gray-400">
                        {section.subheading}
                      </Description>
                    )}
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                      {section.steps?.map((step, sIdx) => (
                        <div
                          key={sIdx}
                          className="group relative overflow-hidden rounded-2xl border border-gray-550 bg-gray-800 p-6 transition-all hover:border-[var(--acc)] hover:shadow-lg"
                        >
                          <div className="mb-4 inline-flex items-center gap-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--acc)]/10 border border-[var(--acc)]/30">
                              <span className="text-sm font-bold text-[var(--acc)]">
                                {sIdx + 1}
                              </span>
                            </div>
                            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--acc)]">
                              {step.phase}
                            </span>
                          </div>

                          <h4 className="mb-3 font-serif text-lg font-light text-white">
                            {step.title}
                          </h4>
                          <p className="mb-4 text-sm text-gray-300 leading-relaxed">
                            {step.description}
                          </p>

                          {step.duration && (
                            <div className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-3 py-1 text-xs text-gray-400">
                              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {step.duration}
                            </div>
                          )}

                          <div className="absolute -bottom-12 -right-12 h-24 w-24 rounded-full bg-[var(--acc)]/5 blur-2xl transition-all group-hover:bg-[var(--acc)]/10"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              );

            case "testimonialsSection":
              return (
                <section key={idx} className="px-4 py-16 md:px-12 md:py-20">
                  <div className="mx-auto max-w-5xl">
                    <Title size="lg" className="mb-12 text-center">
                      {section.heading || "What Our Clients Say"}
                    </Title>
                    <div className="grid gap-8 md:grid-cols-2">
                      {section.testimonials?.map((testimonial, tIdx) => (
                        <div
                          key={tIdx}
                          className="rounded-xl border border-gray-550 bg-gray-800 p-8"
                        >
                          <p className="mb-6 text-lg italic leading-relaxed text-gray-200">
                            "{testimonial.quote}"
                          </p>
                          <div>
                            <div className="font-medium text-white">
                              {testimonial.author}
                            </div>
                            <div className="text-sm text-gray-400">
                              {testimonial.role}, {testimonial.company}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              );

            case "solutionBottomCta":
              return (
                <section key={idx} style={solidCtaBg()} className="px-4 py-20 md:px-12 md:py-28">
                  <div className="mx-auto max-w-4xl text-center">
                    <Title size="lg" className="mb-6">
                      {section.heading}
                    </Title>
                    {section.subheading && (
                      <Description className="mx-auto mb-10 max-w-2xl text-lg">
                        {section.subheading}
                      </Description>
                    )}
                    <div className="flex flex-wrap justify-center gap-4">
                      {section.primaryCta && (
                        <Link to={section.primaryCta.href || "#"}>
                          <Button variant="primary" size="lg">
                            {section.primaryCta.label}
                          </Button>
                        </Link>
                      )}
                      {section.secondaryCta && section.secondaryCta.label && (
                        <Link to={section.secondaryCta.href || "#"}>
                          <Button variant="secondary" size="lg">
                            {section.secondaryCta.label}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </section>
              );

            default:
              return null;
          }
        })}
      </div>
    </>
  );
};

export default SolutionPage;
